import { defer, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, finalize, first, tap } from 'rxjs/operators';

interface PendingTabloSave<T> {
  snapshot: T;
  resolve(): void;
  reject(error: unknown): void;
}

/** Serializes manual and automatic saves; an HTTP unsubscribe cannot undo a write. */
export class TabloProjectSaveQueue<T> {
  private readonly requests$ = new Subject<PendingTabloSave<T>>();
  private readonly pending = new Set<PendingTabloSave<T>>();
  private readonly subscription: Subscription;
  private destroyed = false;

  constructor(save: (snapshot: T) => Observable<unknown>) {
    this.subscription = this.requests$
      .pipe(
        concatMap((request) =>
          defer(() => save(request.snapshot)).pipe(
            first(),
            tap(() => request.resolve()),
            catchError((error: unknown) => {
              request.reject(error);
              return EMPTY;
            }),
            finalize(() => this.pending.delete(request)),
          ),
        ),
      )
      .subscribe();
  }

  save(snapshot: T): Promise<void> {
    if (this.destroyed) {
      return Promise.reject(new Error('The tablo editor has been closed.'));
    }

    return new Promise<void>((resolve, reject) => {
      const request = { snapshot, resolve, reject };
      this.pending.add(request);
      this.requests$.next(request);
    });
  }

  destroy(): void {
    this.destroyed = true;
    // Finalize removes the active request, so reject all promises first.
    this.pending.forEach((request) =>
      request.reject(new Error('The tablo editor has been closed.')),
    );
    this.subscription.unsubscribe();
    this.requests$.complete();
    this.pending.clear();
  }
}
